import type{ ResultObject } from "landing_pages/components/LandingPage"
import type { Variables } from "landing_pages/types"
import { parseHTML } from 'linkedom'

export function sanitize(html: string) {
  let doc: Document

  if (typeof document === 'undefined') {
    // Server-side only
    doc = parseHTML('<body></body>').document
  } else {
    doc = document
  }

  const wrapper = doc.createElement('div')
  wrapper.innerHTML = html

  // Remove all tags except <a>, <b>, <i>, <strong>, <span>
  wrapper.querySelectorAll(':not(a):not(b):not(i):not(strong):not(span)').forEach(node => {
    node.parentNode?.removeChild(node)
  })

  wrapper.querySelectorAll('a').forEach(a => {
    // Remove unsafe hrefs
    const href = a.getAttribute('href')?.trim() ?? ''
    if (href.toLowerCase().startsWith('javascript:')) {
      a.removeAttribute('href')
    }

    // Keep only href attribute, remove any others
    Array.from(a.attributes).forEach(attr => {
      if (attr.name !== 'href') a.removeAttribute(attr.name)
    })

    // Force links to open in new tab safely
    a.setAttribute('target', '_blank')
    a.setAttribute('rel', 'noopener')
  })

  return wrapper.innerHTML
}

export function InnerHTMLHelper({ html }: { html?: string }) {
  return <span dangerouslySetInnerHTML={{__html: sanitize(html ?? '')}}/>
}

export function isPresent(value: unknown) {
  switch(typeof(value)) {
    case 'boolean':
      return value
    case 'string':
      return !!value.trim()
    case 'object':
      return value !== null
    case 'undefined':
      return false
    default:
      return true
  }
}

export function presence<T>(value: T): T | null {
  return isPresent(value) ? value : null
}

export function dig<T>(object: object, path: string): T | null {
  const parts = path.split('.')
  for(let i=0; i<parts.length; ++i) {
    if(typeof(object) !== 'object') {
      return null
    }
    object = object[parts[i]]
  }
  return object as T
}

export function useExpandMacros(result: ResultObject, phoneNumber: string) {
  return (template: string | null | undefined) => {
    if(!template) {
      return ''
    }

    return template
      .replace(/{school\.name}/g, result.campus?.displayName || result.name)
      .replace(/{schoolNames}/g, result.campus?.displayName || result.name) // Used in Eddy API results
      .replace(/{phoneNumber}/g, phoneNumber) // Used in Eddy API results
      .replace(/{mobile-number}{\/mobile-number}/g, phoneNumber) // Used in Eddy API results (may be a bug on Eddy's end, because it's been seen in the wild but doesn't appear in the program directory result)
  }
}

export function useVariable(result: ResultObject, variables: Variables) {
  return <T,>(key: string, defaultValue: T): T => {
    const override = [dig<T>(variables, `clients.${result.id}.${key}`), dig<T>(variables, key)].find(item => (
      item !== undefined && item !== null
    ))

    if(override !== undefined && override !== null) {
      if(defaultValue === undefined || defaultValue === null || typeof(override) === typeof(defaultValue)) {
        return override
      }
    }

    return defaultValue
  }
}

export const isTruthy = (value: unknown) => value !== 'false' && !!value
